import type { ElementType, HTMLAttributes, ReactNode } from "react";
import styles from "./Stack.module.css";
import { cx } from "./cx.js";

export type Space = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type Align = "start" | "center" | "end" | "stretch" | "baseline";
type Justify = "start" | "center" | "end" | "between";

interface FlexProps extends HTMLAttributes<HTMLElement> {
  gap?: Space;
  align?: Align;
  justify?: Justify;
  wrap?: boolean;
  as?: ElementType;
  children?: ReactNode;
}

function useFlex(direction: "vertical" | "horizontal", props: FlexProps) {
  const { gap = 4, align, justify, wrap, as: Tag = "div", className, children, ...rest } = props;
  const classes = cx(
    styles.stack,
    styles[direction],
    styles[`gap-${gap}`],
    align && styles[`align-${align}`],
    justify && styles[`justify-${justify}`],
    wrap && styles.wrap,
    className,
  );
  return { Tag, classes, children, rest };
}

/** Vertical rhythm. Gap defaults to one step, because most stacks want one. */
export function Stack(props: FlexProps) {
  const { Tag, classes, children, rest } = useFlex("vertical", props);
  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}

/** Horizontal. Wrapping is opt-in: silent wrapping hides overflow bugs. */
export function Row(props: FlexProps) {
  const { Tag, classes, children, rest } = useFlex("horizontal", props);
  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}
